import { Dropdown, Menu, Modal, Table, notification } from 'antd';
import moment from 'moment';
import { PureComponent, ReactNode, useState } from 'react';
import { ShepherdOptionsWithType } from 'react-shepherd';
import { Order as HEOrder, OrderDetails } from '../api/fetchAllHEOrder';
import { Order, Payout } from '../api/getPayouts';
import { AgeInYears, getAgeInYears } from '../store/utils';
import { track } from '../tracking';
import { ACTIVE_TAB, ActiveTab, AppointmentWithOrder, OrderStatusFilterType } from './HEDashboard';
import { PopperPlacement } from './trialOrder/Utils';
import getPayoutsReport from '../api/getPayoutsReport';
import { captureError } from './captureError';
import { formattedNumber, openCrispChat } from './utils';
import { AntTooltip } from '../components/common/AntTooltip';
import { OptionItem } from './profile/OptionItem';
import { EditOrderConfig } from '../api/getLabTestsOrder';

export function getSteps(
    setOrderFilter: (orderFilter: OrderStatusFilterType, closeTour?: boolean) => void,
): ShepherdOptionsWithType[] {
    const steps = [
        {
            id: 'pending-orders',
            attachTo: { element: '#pending-orders', on: 'right' as PopperPlacement },
            beforeShowPromise: function () {
                return new Promise<void>(function (resolve) {
                    setTimeout(function () {
                        window.scrollTo(0, 0);
                        resolve();
                    }, 500);
                });
            },
            buttons: [
                {
                    classes: 'shepherd-button-primary',
                    text: 'Next',
                    type: 'next',
                },
            ],
            classes: 'shepherd-tour',
            title: 'Track all your orders here.',
            text: ['Orders placed, until delivered, can be tracked from here.'],
            when: {
                show: () => {
                    setOrderFilter('pending');
                    track('TRIAL_ORDER_CURRENT_STEP', {
                        step: 'pending-orders-tab',
                    });
                },
                hide: () => {
                    console.log('hide step');
                },
                cancel: () => {
                    setOrderFilter('pending', true);
                    track('TRIAL_ORDER_CANCEL_BUTTON_CLICKED', {
                        on: 'pending-orders-tab',
                    });
                },
            },
        },
        {
            id: 'delivered-orders',
            attachTo: { element: '#delivered-orders', on: 'right' as PopperPlacement },
            beforeShowPromise: function () {
                return new Promise<void>(function (resolve) {
                    setTimeout(function () {
                        window.scrollTo(0, 0);
                        resolve();
                    }, 500);
                });
            },
            buttons: [
                {
                    classes: 'shepherd-button-primary',
                    text: 'Back',
                    type: 'back',
                },
                {
                    classes: 'shepherd-button-primary',
                    text: 'Next',
                    type: 'next',
                },
            ],
            classes: 'shepherd-tour',
            title: 'View all delivered orders here',
            text: ['Once delivered, orders are moved to this section.'],
            when: {
                show: () => {
                    setOrderFilter('delivered');
                    track('TRIAL_ORDER_CURRENT_STEP', {
                        step: 'delivered-orders-tab',
                    });
                },
                hide: () => {
                    console.log('hide step');
                },
                cancel: () => {
                    setOrderFilter('delivered', true);
                    track('TRIAL_ORDER_CANCEL_BUTTON_CLICKED', {
                        on: 'delivered-orders-tab',
                    });
                },
            },
        },
        {
            id: 'not-ordered',
            attachTo: { element: '#not-ordered', on: 'right' as PopperPlacement },
            beforeShowPromise: function () {
                return new Promise<void>(function (resolve) {
                    setTimeout(function () {
                        window.scrollTo(0, 0);
                        resolve();
                    }, 500);
                });
            },
            buttons: [
                {
                    classes: 'shepherd-button-primary',
                    text: 'Back',
                    type: 'back',
                },
            ],
            classes: 'shepherd-tour',
            title: 'Prescriptions for which order has yet not been placed are listed here.',
            text: [
                'From here you can place orders for these prescriptions when patients’ visit ends.',
            ],
            when: {
                show: () => {
                    setOrderFilter('not-ordered');
                    track('TRIAL_ORDER_CURRENT_STEP', {
                        step: 'not-ordered-tab',
                    });
                },
                hide: () => {
                    console.log('hide step');
                },
                cancel: () => {
                    setOrderFilter('not-ordered', true);
                    track('TRIAL_ORDER_CANCEL_BUTTON_CLICKED', {
                        on: 'not-ordered-tab',
                    });
                },
            },
        },
    ];

    return steps;
}

export const GetPayoutsModal: React.FC<{
    isVisible: boolean;
    payouts: Payout[];
    closeMe: () => void;
}> = ({ isVisible, payouts, closeMe }) => {
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [isDownloadingReportIndex, setIsDownloadingReportIndex] = useState(-1);

    const columns = [
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Seq</p>,
            dataIndex: 'index',
            key: 'index',
            render: (text: string, record: Payout, index: number): ReactNode => <p>{index + 1}</p>,
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Created At</p>,
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (createdAt: string): ReactNode => (
                <p className="font-700">{moment(createdAt).format('lll')}</p>
            ),
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Purpose</p>,
            dataIndex: 'purpose',
            key: 'purpose',
            width: 165,
            render: (purpose: string): ReactNode => <p>{purpose}</p>,
        },
        {
            title: (
                <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Total Orders</p>
            ),
            dataIndex: 'orders',
            key: 'orders',
            width: 120,
            render: (orders: Order[]): ReactNode => <p className="text-center">{orders.length}</p>,
        },
        {
            title: (
                <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Net Earning</p>
            ),
            dataIndex: 'net_earning',
            key: 'net_earning',
            width: 115,
            render: (netEarning: string): ReactNode => <p className="font-700">₹ {netEarning}</p>,
        },
        {
            title: <p className="font-700 text-12 text-darwin-neutral-700 uppercase">Report</p>,
            dataIndex: 'rowspan',
            key: 'rowspan',
            width: 80,
            render: (rowspan: number, record: Payout, index: number): ReactNode => (
                <div className="flex justify-center">
                    {isDownloadingReportIndex === index ? (
                        <svg
                            className="w-21 h-21 fill-darwin-accent-bright-blue-800 animate-spin"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                    ) : (
                        <button
                            onClick={() => downloadReport(record, index)}
                            disabled={isDownloadingReport}
                            className="disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            <svg
                                className="w-21 h-21 fill-darwin-accent-bright-blue-800"
                                viewBox="0 0 14 14"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M7.60938 1.23438V8.11523L10.0215 5.70312C10.25 5.44922 10.6309 5.44922 10.8848 5.70312C11.1133 5.93164 11.1133 6.3125 10.8848 6.54102L7.40625 9.99414C7.17773 10.248 6.79688 10.248 6.56836 9.99414L3.11523 6.54102C2.86133 6.3125 2.86133 5.93164 3.11523 5.70312C3.34375 5.44922 3.72461 5.44922 3.97852 5.70312L6.39062 8.11523V1.23438C6.39062 0.904297 6.64453 0.625 7 0.625C7.33008 0.625 7.60938 0.904297 7.60938 1.23438ZM3.75 8.34375L4.96875 9.5625H2.125C1.89648 9.5625 1.71875 9.76562 1.71875 9.96875V12C1.71875 12.2285 1.89648 12.4062 2.125 12.4062H11.875C12.0781 12.4062 12.2812 12.2285 12.2812 12V9.96875C12.2812 9.76562 12.0781 9.5625 11.875 9.5625H9.00586L10.2246 8.34375H11.875C12.7637 8.34375 13.5 9.08008 13.5 9.96875V12C13.5 12.9141 12.7637 13.625 11.875 13.625H2.125C1.21094 13.625 0.5 12.9141 0.5 12V9.96875C0.5 9.08008 1.21094 8.34375 2.125 8.34375H3.75ZM11.4688 10.9844C11.4688 11.3398 11.1895 11.5938 10.8594 11.5938C10.5039 11.5938 10.25 11.3398 10.25 10.9844C10.25 10.6543 10.5039 10.375 10.8594 10.375C11.1895 10.375 11.4688 10.6543 11.4688 10.9844Z" />
                            </svg>
                        </button>
                    )}
                </div>
            ),
            onCell: (record: Payout) => ({ rowSpan: Number(record.rowspan) || 0 }),
        },
    ];

    const downloadReport = async (payout: Payout, index: number) => {
        const currentDate = new Date(payout.created_at);
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        setIsDownloadingReport(true);
        setIsDownloadingReportIndex(index);

        try {
            const { success, payout_sheet_url } = await getPayoutsReport(
                String(currentMonth),
                String(currentYear),
            );
            if (success && payout_sheet_url) {
                window.open(payout_sheet_url, '_blank');
                notification.success({
                    message: 'Payout Report has been downloaded successfully!',
                    duration: 5,
                });
            } else {
                notification.error({
                    message: 'No Payout Report found!',
                    duration: 5,
                });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getPayoutsReport',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Failed to fetch Payout Report!',
                duration: 5,
            });
        } finally {
            setIsDownloadingReport(false);
            setIsDownloadingReportIndex(-1);
        }
    };

    const onNeedHelp = () => {
        openCrispChat(
            'NEED_HELP_HXNG',
            {
                from: 'Order Dashboard',
                type: 'payouts',
            },
            `I need help with Clinic-Commerce Payouts`,
        );
    };

    return (
        <Modal
            onCancel={closeMe}
            width={770}
            closable={false}
            visible={isVisible}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: '0px',
            }}
        >
            <div className="flex flex-col p-16 space-y-18 capitalize">
                <div className="flex items-center justify-between font-700 text-darwin-neutral-900 px-5">
                    <p>Earnings for clinic commerce</p>
                    <svg
                        className="w-13 h-13 fill-darwin-neutral-900 cursor-pointer"
                        viewBox="0 0 12 12"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={closeMe}
                    >
                        <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                    </svg>
                </div>

                <div className="w-full max-h-400 overflow-scroll border-b border-darwin-neutral-100">
                    <Table
                        columns={columns}
                        dataSource={payouts}
                        pagination={false}
                        bordered
                        sticky
                        locale={{
                            emptyText: (
                                <div className="flex flex-col items-center space-y-10 py-24">
                                    <svg
                                        className="ant-empty-img-simple w-64 h-40"
                                        viewBox="0 0 64 41"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g
                                            transform="translate(0 1)"
                                            fill="none"
                                            fill-rule="evenodd"
                                        >
                                            <ellipse
                                                className="ant-empty-img-simple-ellipse"
                                                cx="32"
                                                cy="33"
                                                rx="32"
                                                ry="7"
                                            ></ellipse>
                                            <g
                                                className="ant-empty-img-simple-g"
                                                fill-rule="nonzero"
                                            >
                                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                <path
                                                    d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                                                    className="ant-empty-img-simple-path"
                                                ></path>
                                            </g>
                                        </g>
                                    </svg>
                                    <p>No Payouts Yet</p>
                                </div>
                            ),
                        }}
                    />
                </div>
                <div className="flex justify-end pb-2 pr-1">
                    <button
                        className="border border-darwin-primary-500 flex items-center space-x-10 rounded-4 px-12 py-4 font-700"
                        onClick={onNeedHelp}
                    >
                        <p>
                            <svg
                                className="min-w-16 h-18 fill-darwin-primary-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M0 64C0 28.7 28.7 0 64 0H448c35.3 0 64 28.7 64 64V352c0 35.3-28.7 64-64 64H309.3L185.6 508.8c-4.8 3.6-11.3 4.2-16.8 1.5s-8.8-8.2-8.8-14.3V416H64c-35.3 0-64-28.7-64-64V64zm169.8 53.3l-.4 1.2c-4.4 12.5 2.1 26.2 14.6 30.6s26.2-2.1 30.6-14.6l.4-1.2c1.1-3.2 4.2-5.3 7.5-5.3h58.3c8.4 0 15.1 6.8 15.1 15.1c0 5.4-2.9 10.4-7.6 13.1l-44.3 25.4c-7.5 4.3-12.1 12.2-12.1 20.8V216c0 13.3 10.7 24 24 24c13.1 0 23.8-10.5 24-23.6l32.3-18.5c19.6-11.3 31.7-32.2 31.7-54.8c0-34.9-28.3-63.1-63.1-63.1H222.6c-23.7 0-44.8 14.9-52.8 37.3zM288 304a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
                            </svg>
                        </p>
                        <p className="text-darwin-primary-500">Issue with Payouts?</p>
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const DELIVERED = 'delivered';
const PARTIALLY_DELIVERED = 'partially_delivered';

export const FeelGoodComponent: React.FC<{
    transactions: HEOrder[];
    showFeelGoodData: boolean;
    toggleFeelGoodData: () => void;
}> = ({ transactions, showFeelGoodData, toggleFeelGoodData }) => {
    const deliveredOrdersArray = transactions.filter(
        (i) => i.status === DELIVERED && i.sub_status !== PARTIALLY_DELIVERED,
    );
    const deliveredOrders = deliveredOrdersArray.length;
    const deliveredEarlyArray = deliveredOrdersArray.filter(
        (i) => i?.order_details?.delivered_early_by,
    );
    const deliveredEarly = deliveredEarlyArray.length;
    const amountSavedByPatients = deliveredOrdersArray.reduce((acc, cur) => {
        const { amount = 0, paidAmount = 0 } = cur;
        return acc + amount - paidAmount;
    }, 0);
    const seniorCitizensServed = deliveredOrdersArray.reduce((acc, cur) => {
        const { appointment_details: { patient: dob = {} } = {} } = cur;
        return acc + (getAgeInYears(dob as AgeInYears) >= 60 ? 1 : 0);
    }, 0);
    const seniorCitizensWithDeliveredEarly = deliveredOrdersArray.reduce((acc, cur) => {
        const { appointment_details: { patient: dob = {} } = {}, order_details } = cur;
        return (
            acc +
            (getAgeInYears(dob as AgeInYears) >= 60 && order_details?.delivered_early_by ? 1 : 0)
        );
    }, 0);
    const happyPatients = deliveredEarly + seniorCitizensServed - seniorCitizensWithDeliveredEarly;
    return happyPatients > 0 ? (
        <div className="relative">
            <div
                className="h-74 px-12 py-8 flex items-start space-x-10 rounded-4 cursor-pointer"
                style={{
                    color: '#1A1A1A',
                    background:
                        'var(--Gradient-green, linear-gradient(100deg, #95E9C1 2.51%, #D8FF71 134.8%))',
                    position: 'relative',
                }}
                onClick={toggleFeelGoodData}
            >
                <div className="pt-3">
                    <p className="text-18">😀</p>
                </div>
                <div className="space-y-4">
                    <p className="font-700">Happy patients</p>
                    <p className="text-21 font-700">{formattedNumber(happyPatients)}</p>
                </div>
                <div className="pt-6 cursor-pointer">
                    <svg
                        className="w-16 h-16"
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.99023 6.27148L12.418 2.84375H10.2344C9.87891 2.84375 9.625 2.58984 9.625 2.23438C9.625 1.9043 9.87891 1.625 10.2344 1.625H13.8652C14.2207 1.625 14.4746 1.9043 14.4746 2.23438V5.89062C14.4746 6.24609 14.2207 6.5 13.8652 6.5C13.5352 6.5 13.2559 6.24609 13.2559 5.89062V3.70703L9.82812 7.13477C9.59961 7.38867 9.21875 7.38867 8.99023 7.13477C8.73633 6.90625 8.73633 6.52539 8.99023 6.27148ZM7.00977 9.97852L3.55664 13.4062H5.76562C6.0957 13.4062 6.375 13.6855 6.375 14.0156C6.375 14.3711 6.0957 14.625 5.76562 14.625H2.10938C1.75391 14.625 1.5 14.3711 1.5 14.0156V10.3594C1.5 10.0293 1.75391 9.75 2.10938 9.75C2.43945 9.75 2.71875 10.0293 2.71875 10.3594V12.5684L6.14648 9.14062C6.375 8.88672 6.75586 8.88672 7.00977 9.14062C7.23828 9.36914 7.23828 9.75 7.00977 9.97852Z"
                            fill="#767676"
                        />
                    </svg>
                </div>
            </div>

            {showFeelGoodData && (
                <div
                    className="absolute top-0 left-0 p-16 rounded-4 space-y-16 z-50"
                    style={{
                        color: '#1A1A1A',
                        background:
                            'var(--Gradient-green, linear-gradient(100deg, #95E9C1 2.51%, #D8FF71 134.8%))',
                        width: 350,
                    }}
                >
                    <div className="flex justify-between w-full">
                        <div className="flex space-x-10">
                            <div className="pt-3">
                                <p className="text-18">😀</p>
                            </div>
                            <div className="space-y-4">
                                <p className="font-700 whitespace-nowrap">Happy patients</p>
                                <p className="text-32 font-700">{formattedNumber(happyPatients)}</p>
                            </div>
                        </div>
                        <div className="pt-2 cursor-pointer" onClick={toggleFeelGoodData}>
                            <svg
                                className="w-24 h-24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M16.7812 8.28125L13.0312 12.0312L16.75 15.75C17.0625 16.0312 17.0625 16.5 16.75 16.7812C16.4688 17.0938 16 17.0938 15.7188 16.7812L11.9688 13.0625L8.25 16.7812C7.96875 17.0938 7.5 17.0938 7.21875 16.7812C6.90625 16.5 6.90625 16.0312 7.21875 15.7188L10.9375 12L7.21875 8.28125C6.90625 8 6.90625 7.53125 7.21875 7.21875C7.5 6.9375 7.96875 6.9375 8.28125 7.21875L12 10.9688L15.7188 7.25C16 6.9375 16.4688 6.9375 16.7812 7.25C17.0625 7.53125 17.0625 8 16.7812 8.28125Z"
                                    fill="#484848"
                                />
                            </svg>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-16">
                        {amountSavedByPatients > 0 && (
                            <div className="p-8 flex items-start space-x-8 rounded-4 bg-white">
                                <div className="pt-4">
                                    <svg
                                        className="w-10 h-12"
                                        viewBox="0 0 10 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M0.9375 1.25C0.9375 0.818359 1.29297 0.4375 1.75 0.4375H3.375H3.78125H8.25C8.68164 0.4375 9.0625 0.818359 9.0625 1.25C9.0625 1.70703 8.68164 2.0625 8.25 2.0625H6.80273C7.05664 2.44336 7.23438 2.84961 7.33594 3.28125H8.25C8.68164 3.28125 9.0625 3.66211 9.0625 4.09375C9.0625 4.55078 8.68164 4.90625 8.25 4.90625H7.33594C7.00586 6.4043 5.76172 7.54688 4.23828 7.72461L7.89453 10.3398C8.27539 10.6191 8.35156 11.127 8.09766 11.4824C7.81836 11.8379 7.31055 11.9395 6.95508 11.6602L1.26758 7.59766C0.988281 7.39453 0.861328 7.03906 0.962891 6.70898C1.06445 6.35352 1.39453 6.125 1.75 6.125H3.78125C4.59375 6.125 5.33008 5.64258 5.63477 4.90625H1.75C1.29297 4.90625 0.9375 4.55078 0.9375 4.09375C0.9375 3.66211 1.29297 3.28125 1.75 3.28125H5.63477C5.33008 2.57031 4.59375 2.0625 3.78125 2.0625H3.375H1.75C1.29297 2.0625 0.9375 1.70703 0.9375 1.25Z"
                                            fill="url(#paint0_linear_5240_1394)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_5240_1394"
                                                x1="-0.323944"
                                                y1="-2.25"
                                                x2="13.6537"
                                                y2="-0.948258"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#215FFF" />
                                                <stop offset="1" stop-color="#143999" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-13 " style={{ color: '#484848' }}>
                                        Amount saved by patients
                                    </p>
                                    <p className="text-20 font-700">
                                        ₹{formattedNumber(Math.round(amountSavedByPatients))}
                                    </p>
                                </div>
                            </div>
                        )}

                        {seniorCitizensServed > 0 && (
                            <div className="p-8 flex items-start space-x-8 rounded-4 bg-white">
                                <div className="pt-1">
                                    <svg
                                        className="w-16 h-18"
                                        viewBox="0 0 16 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M9.21875 3.84375C9.21875 4.30078 8.96484 4.68164 8.60938 4.91016C8.22852 5.13867 7.74609 5.13867 7.39062 4.91016C7.00977 4.68164 6.78125 4.30078 6.78125 3.84375C6.78125 3.41211 7.00977 3.03125 7.39062 2.80273C7.74609 2.57422 8.22852 2.57422 8.60938 2.80273C8.96484 3.03125 9.21875 3.41211 9.21875 3.84375ZM9.01562 8.61719V14.8125C9.01562 15.2695 8.63477 15.625 8.20312 15.625C7.74609 15.625 7.39062 15.2695 7.39062 14.8125V11.5625H6.98438V14.8125C6.98438 15.2695 6.60352 15.625 6.17188 15.625C5.71484 15.625 5.35938 15.2695 5.35938 14.8125V9.15039L4.62305 10.3691C4.39453 10.75 3.88672 10.877 3.50586 10.6484C3.125 10.4199 2.99805 9.91211 3.22656 9.53125L4.69922 7.06836C5.15625 6.33203 5.94336 5.875 6.80664 5.875H7.7207C8.43164 5.875 9.11719 6.20508 9.59961 6.76367L11.4531 9.02344C11.7578 9.37891 11.707 9.88672 11.3516 10.166C10.9961 10.4453 10.4883 10.3945 10.209 10.0645L9.01562 8.61719ZM11.25 12.1719V12.375C11.25 12.7305 10.9707 12.9844 10.6406 12.9844C10.2852 12.9844 10.0312 12.7305 10.0312 12.375V12.1719C10.0312 11.4102 10.666 10.75 11.4531 10.75C12.2148 10.75 12.875 11.4102 12.875 12.1719V15.0156C12.875 15.3711 12.5957 15.625 12.2656 15.625C11.9102 15.625 11.6562 15.3711 11.6562 15.0156V12.1719C11.6562 12.0703 11.5547 11.9688 11.4531 11.9688C11.3262 11.9688 11.25 12.0703 11.25 12.1719Z"
                                            fill="url(#paint0_linear_5212_3896)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_5212_3896"
                                                x1="0.901408"
                                                y1="0.750001"
                                                x2="19.4145"
                                                y2="3.04883"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#215FFF" />
                                                <stop offset="1" stop-color="#143999" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-13 " style={{ color: '#484848' }}>
                                        Senior citizens served
                                    </p>
                                    <p className="text-20 font-700">
                                        {formattedNumber(seniorCitizensServed)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {deliveredOrders > 0 && (
                            <div className="p-8 flex items-start space-x-8 rounded-4 bg-white">
                                <div className="pt-1">
                                    <svg
                                        className="w-16 h-18"
                                        viewBox="0 0 16 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M8 15.625C5.66406 15.625 3.53125 14.4062 2.36328 12.375C1.19531 10.3691 1.19531 7.90625 2.36328 5.875C3.53125 3.86914 5.66406 2.625 8 2.625C10.3105 2.625 12.4434 3.86914 13.6113 5.875C14.7793 7.90625 14.7793 10.3691 13.6113 12.375C12.4434 14.4062 10.3105 15.625 8 15.625ZM10.8691 7.93164H10.8438C11.0977 7.70312 11.0977 7.32227 10.8438 7.06836C10.6152 6.83984 10.2344 6.83984 10.0059 7.06836L7.1875 9.91211L5.99414 8.71875C5.74023 8.46484 5.35938 8.46484 5.13086 8.71875C4.87695 8.94727 4.87695 9.32812 5.13086 9.55664L6.75586 11.1816C6.98438 11.4355 7.36523 11.4355 7.61914 11.1816L10.8691 7.93164Z"
                                            fill="url(#paint0_linear_5212_3902)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_5212_3902"
                                                x1="0.309859"
                                                y1="0.750001"
                                                x2="20.3129"
                                                y2="3.44084"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#215FFF" />
                                                <stop offset="1" stop-color="#143999" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-13 " style={{ color: '#484848' }}>
                                        Deliveries made
                                    </p>
                                    <p className="text-20 font-700">
                                        {formattedNumber(deliveredOrders)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {deliveredEarly > 0 && (
                            <div
                                className="p-8 flex items-start space-x-8 rounded-4"
                                style={{
                                    background:
                                        'var(--Gradient-black, linear-gradient(100deg, #373737 3.91%, #1A1A1A 51.96%, #373737 100%))',
                                }}
                            >
                                <div className="pt-1">
                                    <svg
                                        className="w-10 h-14"
                                        viewBox="0 0 10 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M8.17383 1.76758L6.21875 6.33789H9.0625C9.39258 6.33789 9.69727 6.54102 9.82422 6.8457C9.92578 7.17578 9.84961 7.53125 9.5957 7.75977L3.0957 13.4473C2.79102 13.6758 2.38477 13.7012 2.08008 13.4727C1.77539 13.2695 1.64844 12.8633 1.80078 12.5078L3.75586 7.9375H0.9375C0.582031 7.9375 0.277344 7.73438 0.175781 7.42969C0.0488281 7.09961 0.125 6.74414 0.378906 6.51562L6.87891 0.828125C7.18359 0.599609 7.58984 0.574219 7.89453 0.802734C8.19922 1.00586 8.32617 1.41211 8.17383 1.76758Z"
                                            fill="url(#paint0_linear_5240_1762)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_5240_1762"
                                                x1="-0.571429"
                                                y1="1.75"
                                                x2="12.6981"
                                                y2="4.19598"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#CCA557" />
                                                <stop offset="0.505208" stop-color="#FFE5AE" />
                                                <stop offset="1" stop-color="#CCA557" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-13 " style={{ color: '#D1D1D1' }}>
                                        Delivered early
                                    </p>
                                    <p className="text-20 font-700 text-white">
                                        {formattedNumber(deliveredEarly)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    ) : null;
};

export const GetCurrentOrderStatus: React.FC<{
    order_details: HEOrder;
}> = ({ order_details }) => {
    const { status, current_order_status } = order_details;
    if (!current_order_status || status === DELIVERED) return null;
    return (
        <AntTooltip content={current_order_status} placement="bottom">
            <div className="text-13 font-700 w-26 h-26 flex items-center justify-center rounded-4 bg-darwin-accent-investigations-ochre-800">
                <svg
                    className="w-16 h-16 fill-white"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M13.2812 8.125C13.2812 6.24609 12.2656 4.51953 10.6406 3.55469C8.99023 2.61523 6.98438 2.61523 5.35938 3.55469C3.70898 4.51953 2.71875 6.24609 2.71875 8.125C2.71875 10.0293 3.70898 11.7559 5.35938 12.7207C6.98438 13.6602 8.99023 13.6602 10.6406 12.7207C12.2656 11.7559 13.2812 10.0293 13.2812 8.125ZM1.5 8.125C1.5 5.81445 2.71875 3.68164 4.75 2.51367C6.75586 1.3457 9.21875 1.3457 11.25 2.51367C13.2559 3.68164 14.5 5.81445 14.5 8.125C14.5 10.4609 13.2559 12.5938 11.25 13.7617C9.21875 14.9297 6.75586 14.9297 4.75 13.7617C2.71875 12.5938 1.5 10.4609 1.5 8.125ZM7.39062 4.67188C7.39062 4.3418 7.64453 4.0625 8 4.0625C8.33008 4.0625 8.60938 4.3418 8.60938 4.67188V7.82031L10.7676 9.24219C11.0469 9.44531 11.123 9.82617 10.9453 10.1055C10.7422 10.3848 10.3613 10.4609 10.082 10.2578L7.64453 8.63281C7.49219 8.53125 7.39062 8.32812 7.39062 8.125V4.67188Z" />
                </svg>
            </div>
        </AntTooltip>
    );
};

interface Props {
    order: HEOrder;
    activeTab: ActiveTab;
    isCancellationNotAllowed: boolean;
    toggleEditOrder: (order?: HEOrder) => void;
    toggleActiveOrder: (order?: OrderDetails) => void;
    showApptDialogVisible: (appt: AppointmentWithOrder, isForEditOrder?: boolean) => void;
}

interface State {
    visible: boolean;
}

export class MoreOptionsDropdown extends PureComponent<Props> {
    state: State = {
        visible: false,
    };

    onVisibleChange = (visible: boolean): void => {
        this.setState({
            visible: visible,
        });
    };

    onClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        const { onVisibleChange } = this;
        e.stopPropagation();
        onVisibleChange(false);
    };

    render(): ReactNode {
        const {
            order,
            activeTab,
            isCancellationNotAllowed,
            toggleEditOrder,
            toggleActiveOrder,
            showApptDialogVisible,
        } = this.props;
        const { visible } = this.state;
        const { onVisibleChange, onClick } = this;
        const editOrderConfig = order ? order.edit_order_config : {};
        const {
            canUpdateSlot,
            canUpdateTest,
            canUpdatePaymode,
            canUpdateDiscount,
            canUpdateAddress,
            canUpdatePatientDetails,
        } = (editOrderConfig || {}) as EditOrderConfig;
        const canEditOrder =
            canUpdateSlot ||
            canUpdateTest ||
            canUpdatePaymode ||
            canUpdateDiscount ||
            canUpdateAddress ||
            canUpdatePatientDetails;

        return (
            <Dropdown
                overlay={
                    <Menu>
                        <OptionItem
                            onClick={(e) => {
                                onClick(e);
                                showApptDialogVisible((order as unknown) as AppointmentWithOrder);
                                track('HE_DASHBOARD_VIEW_ORDER_BUTTON', {
                                    pxId: order.prescription_id || '',
                                    activeTab: activeTab,
                                });
                            }}
                            label={'View Order'}
                            leftIcon={
                                <svg
                                    className="w-16 h-16 fill-darwin-accent-bright-blue-800"
                                    viewBox="0 0 9 9"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M5.15625 1.13672L8.59375 4.41797C8.69141 4.51562 8.75 4.63281 8.75 4.76953C8.75 4.88672 8.69141 5.00391 8.59375 5.10156L5.15625 8.38281C4.98047 8.55859 4.66797 8.55859 4.49219 8.36328C4.31641 8.1875 4.31641 7.875 4.51172 7.69922L7.10938 5.23828H0.46875C0.195312 5.23828 0 5.02344 0 4.76953C0 4.49609 0.195312 4.30078 0.46875 4.30078H7.10938L4.51172 1.82031C4.31641 1.64453 4.31641 1.33203 4.49219 1.15625C4.66797 0.960938 4.96094 0.960938 5.15625 1.13672Z" />
                                </svg>
                            }
                        />
                        {order.type === ACTIVE_TAB.LABS && (
                            <OptionItem
                                onClick={(e) => {
                                    onClick(e);
                                    toggleEditOrder(order);
                                    track('HE_DASHBOARD_RESCHEDULE_SLOT_BUTTON', {
                                        pxId: order.prescription_id || '',
                                        activeTab: activeTab,
                                    });
                                }}
                                label={'Reschedule slot'}
                                leftIcon={
                                    <svg
                                        className="w-16 h-16 fill-darwin-accent-bright-blue-800"
                                        viewBox="0 0 14 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M13.0938 4.70312C13.0938 5.05859 12.8145 5.3125 12.4844 5.3125H9.64062H8.82812C8.47266 5.3125 8.21875 5.05859 8.21875 4.70312C8.21875 4.37305 8.47266 4.09375 8.82812 4.09375H9.64062H11.0117L9.69141 2.77344C8.95508 2.0625 7.99023 1.65625 7 1.65625C5.14648 1.65625 3.54688 2.79883 2.88672 4.39844C2.73438 4.70312 2.37891 4.83008 2.07422 4.70312C1.76953 4.57617 1.61719 4.2207 1.74414 3.91602C2.60742 1.88477 4.63867 0.4375 7 0.4375C8.32031 0.4375 9.58984 0.970703 10.5293 1.91016L11.875 3.25586V1.85938V1.04688C11.875 0.716797 12.1289 0.4375 12.4844 0.4375C12.8145 0.4375 13.0938 0.716797 13.0938 1.04688V4.70312ZM1.51562 6.9375H4.35938C4.68945 6.9375 4.96875 7.2168 4.96875 7.54688C4.96875 7.90234 4.68945 8.15625 4.35938 8.15625H2.96289L4.30859 9.50195C5.01953 10.2129 5.98438 10.5938 7 10.5938C8.82812 10.5938 10.4277 9.50195 11.0879 7.90234C11.2402 7.57227 11.5957 7.44531 11.9004 7.57227C12.2051 7.69922 12.3574 8.05469 12.2305 8.38477C11.3418 10.3906 9.33594 11.8125 7 11.8125C5.6543 11.8125 4.38477 11.3047 3.44531 10.3652L2.125 9.01953V10.3906C2.125 10.7461 1.8457 11 1.51562 11C1.16016 11 0.90625 10.7461 0.90625 10.3906V7.54688C0.90625 7.2168 1.16016 6.9375 1.51562 6.9375Z" />
                                    </svg>
                                }
                                disabled={!canUpdateSlot}
                            />
                        )}
                        {order.type === ACTIVE_TAB.LABS && (
                            <OptionItem
                                onClick={(e) => {
                                    onClick(e);
                                    showApptDialogVisible(
                                        (order as unknown) as AppointmentWithOrder,
                                        true,
                                    );
                                    track('HE_DASHBOARD_EDIT_ORDER_BUTTON', {
                                        pxId: order.prescription_id || '',
                                        activeTab: activeTab,
                                    });
                                }}
                                label={'Edit order'}
                                leftIcon={
                                    <svg
                                        className="w-16 h-16 fill-darwin-accent-bright-blue-800"
                                        viewBox="0 0 14 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M1.41406 9.79102C1.51562 9.43555 1.71875 9.10547 1.97266 8.85156L9.69141 1.13281C10.3262 0.498047 11.3672 0.498047 12.002 1.13281L12.9922 2.12305C13.0684 2.19922 13.1445 2.30078 13.1953 2.37695C13.627 3.01172 13.5508 3.875 12.9922 4.43359L5.27344 12.1523C5.24805 12.1777 5.19727 12.2031 5.17188 12.2539C4.91797 12.457 4.63867 12.6094 4.33398 12.7109L2.35352 13.2949L1.26172 13.625C1.05859 13.6758 0.830078 13.625 0.677734 13.4473C0.5 13.2949 0.449219 13.0664 0.525391 12.8633L0.830078 11.7715L1.41406 9.79102ZM2.58203 10.1465L2.4043 10.7305L1.99805 12.127L3.39453 11.7207L3.97852 11.543C4.15625 11.4922 4.2832 11.416 4.41016 11.2891L10.2246 5.47461L8.65039 3.90039L2.83594 9.71484C2.81055 9.71484 2.81055 9.74023 2.78516 9.76562C2.68359 9.86719 2.63281 9.99414 2.58203 10.1465Z" />
                                    </svg>
                                }
                                disabled={!canEditOrder}
                            />
                        )}
                        <OptionItem
                            onClick={(e) => {
                                onClick(e);
                                if (isCancellationNotAllowed) {
                                    notification.error({
                                        message:
                                            order.status === 'delivered' ||
                                            order.status === 'cancelled'
                                                ? `Cancellation not possible for ${order.status} orders`
                                                : 'Cancellation not possible after 15 mins',
                                        duration: 5,
                                    });
                                    return;
                                }
                                toggleActiveOrder((order as unknown) as OrderDetails);
                            }}
                            label={`Cancel ${
                                order.type === ACTIVE_TAB.LABS ? 'Booking' : 'Order'
                            } `}
                            leftIcon={
                                <svg
                                    className="w-18 h-18 fill-darwin-status-warning-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 384 512"
                                >
                                    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
                                </svg>
                            }
                        />
                    </Menu>
                }
                placement="bottomLeft"
                arrow
                visible={visible}
                onVisibleChange={onVisibleChange}
            >
                <button
                    className="flex items-center justify-center h-34 px-8 rounded-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg
                        className="w-5 h-14 fill-darwin-accent-bright-blue-800"
                        viewBox="0 0 4 13"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M2 9.59375C2.82031 9.59375 3.53125 10.3047 3.53125 11.125C3.53125 11.9727 2.82031 12.6562 2 12.6562C1.15234 12.6562 0.46875 11.9727 0.46875 11.125C0.46875 10.3047 1.15234 9.59375 2 9.59375ZM2 5.21875C2.82031 5.21875 3.53125 5.92969 3.53125 6.75C3.53125 7.59766 2.82031 8.28125 2 8.28125C1.15234 8.28125 0.46875 7.59766 0.46875 6.75C0.46875 5.92969 1.15234 5.21875 2 5.21875ZM3.53125 2.375C3.53125 3.22266 2.82031 3.90625 2 3.90625C1.15234 3.90625 0.46875 3.22266 0.46875 2.375C0.46875 1.55469 1.15234 0.84375 2 0.84375C2.82031 0.84375 3.53125 1.55469 3.53125 2.375Z" />
                    </svg>
                </button>
            </Dropdown>
        );
    }
}
